import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import {
  ApiError,
  API_URL,
  downloadBlob,
  downloadText,
  exportPayrollReport,
  validatePayrollExport,
  type PayrollValidateResult,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getLastNDaysRange, toISODate } from "@/lib/time";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";
import { appendRepeatQuery } from "@/pages/dashboard/admin/ReportMultiSelect";

type PayWeekItem = { pay_week: string; from_date: string; to_date: string; label: string };

export function usePayrollExportViewModel() {
  const { user } = useAuth();
  const { isAdmin } = useDashboardRoles();
  const [payrollFormat, setPayrollFormat] = React.useState<"csv" | "xlsx">("csv");
  const [payrollCoCode, setPayrollCoCode] = React.useState<string[]>([]);
  const [payrollOfficeLocation, setPayrollOfficeLocation] = React.useState<string[]>([]);
  const [payrollExportError, setPayrollExportError] = React.useState<string | null>(null);
  const [payrollValidate, setPayrollValidate] = React.useState<PayrollValidateResult | null>(null);
  const [payrollValidateLoading, setPayrollValidateLoading] = React.useState(false);
  const [payrollRequireApprovals, setPayrollRequireApprovals] = React.useState(false);
  const [payrollPayWeek, setPayrollPayWeek] = React.useState<string>("");
  const [fromDate, setFromDate] = React.useState(() => getLastNDaysRange(7).from);
  const [toDate, setToDate] = React.useState(() => getLastNDaysRange(7).to);
  const [payWeekDefaultApplied, setPayWeekDefaultApplied] = React.useState(false);
  const currentYear = React.useMemo(() => new Date().getFullYear(), []);

  const {
    data: payrollCoCodesData,
    isLoading: payrollCoCodesLoading,
    error: payrollCoCodesError,
  } = useQuery({
    queryKey: ["payroll-co-codes"],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/co-codes`, { credentials: "include" });
      if (!resp.ok) return { co_codes: [] as string[], companies: {} as Record<string, string> };
      return resp.json() as Promise<{ co_codes: string[]; companies?: Record<string, string> }>;
    },
    enabled: !!isAdmin,
  });

  const {
    data: officeLocationsData,
    isLoading: officeLocationsLoading,
    error: officeLocationsError,
  } = useQuery({
    queryKey: ["office-locations"],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/office-locations`, { credentials: "include" });
      if (!resp.ok) return { office_locations: [] as string[] };
      return resp.json() as Promise<{ office_locations: string[] }>;
    },
    enabled: !!isAdmin,
  });

  const {
    data: payWeeksData,
    isLoading: payWeeksLoading,
    error: payWeeksError,
  } = useQuery({
    queryKey: ["pay-weeks", currentYear],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/pay-weeks?year=${currentYear}`, { credentials: "include" });
      if (!resp.ok) return { pay_weeks: [] as PayWeekItem[], current: null as PayWeekItem | null };
      return resp.json() as Promise<{ pay_weeks: PayWeekItem[]; current: PayWeekItem | null }>;
    },
    enabled: !!user,
  });

  React.useEffect(() => {
    if (payWeekDefaultApplied || !payWeeksData?.current) return;
    const c = payWeeksData.current;
    setPayrollPayWeek(c.pay_week);
    setFromDate(c.from_date);
    setToDate(c.to_date);
    setPayWeekDefaultApplied(true);
  }, [payWeeksData, payWeekDefaultApplied]);

  function buildPayrollParams(): URLSearchParams {
    const params = new URLSearchParams();
    const from = fromDate || toISODate(new Date(Date.now() - 30 * 86400000));
    const to = toDate || toISODate(new Date());
    params.set("from_date", from);
    params.set("to_date", to);
    appendRepeatQuery(params, "co_code", payrollCoCode);
    appendRepeatQuery(params, "office_location", payrollOfficeLocation);
    if (payrollPayWeek) params.set("pay_week", payrollPayWeek);
    return params;
  }

  async function handlePayrollValidate() {
    if (!isAdmin) return;
    setPayrollExportError(null);
    setPayrollValidateLoading(true);
    setPayrollValidate(null);
    try {
      const result = await validatePayrollExport(buildPayrollParams());
      setPayrollValidate(result);
    } catch (error) {
      if (error instanceof ApiError) {
        setPayrollExportError(error.status === 403 ? "Admin access required for validate." : (error.message || "Validate failed"));
      } else {
        setPayrollExportError("Validate failed.");
      }
    } finally {
      setPayrollValidateLoading(false);
    }
  }

  async function handlePayrollExport() {
    if (!isAdmin) return;
    setPayrollExportError(null);
    try {
      const params = buildPayrollParams();
      const from = params.get("from_date") || "";
      const to = params.get("to_date") || "";
      const result = await exportPayrollReport(params, payrollFormat, {
        requireApprovals: payrollRequireApprovals,
      });

      // Determine filename:
      // - If pay week selected: EPI{co}{payWeek}
      // - Else: include the selected date range
      let base: string;
      if (payrollPayWeek) {
        base = `EPI${payrollCoCode.join("-")}${payrollPayWeek}`;
      } else {
        const coPart = payrollCoCode.length ? `${payrollCoCode.join("-")}_` : "";
        base = `payroll_${coPart}${from}_to_${to}`;
      }

      if (payrollFormat === "xlsx") {
        downloadBlob(`${base}.xlsx`, result as Blob);
      } else {
        downloadText(`${base}.csv`, result as string, "text/csv;charset=utf-8");
      }
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 409) {
          const d = error.detail as {
            message?: string;
            detail?: { message?: string } | string;
          } | string;
          let msg = "Approvals incomplete for this period.";
          if (typeof d === "string") msg = d;
          else if (d && typeof d === "object") {
            const inner = d.detail;
            if (typeof inner === "object" && inner?.message) msg = inner.message;
            else if (typeof inner === "string") msg = inner;
            else if (d.message) msg = d.message;
          }
          setPayrollExportError(msg);
        } else if (error.status === 403) {
          setPayrollExportError("This report is restricted to users in the admin Azure group.");
        } else if (error.status === 429) {
          setPayrollExportError("Rate limited. Please wait a minute.");
        } else {
          setPayrollExportError(error.message || "Payroll export failed.");
        }
      } else {
        setPayrollExportError("Payroll export failed.");
      }
    }
  }


  return {
    isAdmin,
    payrollFormat, setPayrollFormat,
    payrollCoCode, setPayrollCoCode,
    payrollOfficeLocation, setPayrollOfficeLocation,
    payrollExportError, setPayrollExportError,
    payrollValidate, setPayrollValidate,
    payrollValidateLoading,
    payrollRequireApprovals, setPayrollRequireApprovals,
    payrollPayWeek, setPayrollPayWeek,
    fromDate, setFromDate,
    toDate, setToDate,
    payrollCoCodesData, payrollCoCodesLoading, payrollCoCodesError,
    officeLocationsData, officeLocationsLoading, officeLocationsError,
    payWeeksData, payWeeksLoading, payWeeksError,
    handlePayrollValidate, handlePayrollExport,
  };
}
